import { createHead, VueHeadMixin } from '@unhead/vue/client'
import { RouterView } from 'vue-router'
import type { Component } from 'vue'
import { getAppContextBrowser } from './js/services/appContext'
import { createApp } from './app'
import { Rogue } from './js/services/Rogue'
import { createRouter } from './js/router/router'
import { RogueApi } from './js/services/api/api'
import { RogueRfk } from './js/services/rfk/rfk'
import { RogueConstructor } from './js/services/cnstrc/constructor'
import { useListStore } from '@/js/stores/listStore'
import { intercept } from '@/js/helpers/internal-link'
import { BrowserLog, setBrowserRogue, setBrowserVueApp } from '@/js/services/log/browser-log'
import { axiosInstance } from '@/js/services/api/axios-api'
import { Analytics } from '@/js/services/analytics/analytics'
import { setCookie } from '@/js/services/Utils'
import { CookieStickyBucketService } from '@/js/services/stickyBuckets'
import { configureErrorHandler } from '@/js/helpers/vue-error-handler'
import { loadScript } from '@/js/helpers/scripts'
import iconSprites from 'virtual:icon-sprites'

if (!window.Promise) {
  // @todo Show compatability banner
}

try {
  if (window.localStorage.getItem('_rg_dbg') === '1') {
    window.ROGUE._dbg = true
  }
} catch (e) {
  //
}

window.ROGUE_RENDER_START = window.ROGUE_RENDER_START || Date.now()
window.ROGUE.sessionStart = Date.now()
window.ROGUE.url = window.location.toString()

const { env, site, clientKeys } = window.ROGUE

const appContext = getAppContextBrowser()

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  if (reason instanceof Error) {
    console.error('UNHANDLED REJECTION:', reason.message, reason.stack)
  } else {
    console.error('UNHANDLED REJECTION:', reason)
  }
})

window.addEventListener('error', (event) => {
  const { message, filename, lineno, colno, error } = event
  if (error instanceof Error) {
    console.error('UNHANDLED ERROR:', error.message, `at ${filename}:${lineno}:${colno}`, error.stack)
  } else {
    console.error('UNHANDLED ERROR:', message, `at ${filename}:${lineno}:${colno}`)
  }
})

const rogue = new Rogue({
  site,
  keys                : clientKeys,
  appContext,
  features            : window.ROGUE.features,
  featuresUpdatedDate : window.ROGUE.featuresUpdatedDate,
  forceFeatures       : window.ROGUE.forceFeatures || {},
  debug               : window.ROGUE._dbg,
  baseUrl             : window.location.origin.toString(),
  analytics           : new Analytics(),
  initialRoute        : window.location.toString(),
  // For SSR, these will come in via the window.ROGUE object.
  l10n                : window.ROGUE.l10n,
  env,
  ssr                 : false,
  log                 : BrowserLog,
  api                 : new RogueApi(axiosInstance),
  rfk                 : new RogueRfk(),
  cnstrc              : new RogueConstructor(),
  conversionRates     : window.ROGUE.conversionRates || {},
  stickyBuckets       : new CookieStickyBucketService(),
})

if (window.ROGUE.url.indexOf(window.ROGUE.baseUrl) !== 0) {
  rogue.api.fetch({ method: 'POST', url: `${window.ROGUE.baseUrl}/svc_ssr/hostmatch`, data: { h: window.ROGUE.url, b: window.ROGUE.baseUrl } })
    .catch(err => rogue.log.warn('Error with hostmatch', err))
}

// Needed to fix a weird dependency cycle between Rogue and BrowserLog...
setBrowserRogue(rogue)

axiosInstance.rogue = rogue

window.__deleteCart = async () => {
  const listStore = useListStore(rogue.pinia)
  const cartId = listStore.cart._id
  if (cartId) {
    await listStore.deleteList(rogue, cartId)
  }
}

if (window.navigator.onLine !== undefined) {
  window.addEventListener('offline', () => rogue.setNetworkStatus(false))
  window.addEventListener('online', () => rogue.setNetworkStatus(true))
}

let initApp: Promise<any> = Promise.resolve()
let appBridge: any
const appMessageQueue: { message: string, data: any }[] = []

if (appContext) {
  // Apply the platform class sooner
  const rootEl = document.getElementsByTagName('html')[0]
  rootEl.classList.add(`platform-${appContext.platform}`)
  rootEl.classList.add('rogue-app-rn', `rogue-app-${appContext.version}`)

  initApp = import(/* webpackChunkName: "app-bridge" */'@/js/app-bridge/index')
    .then(bridge => appBridge = bridge.default)
    .catch(err => rogue.log.error('ERROR LOADING APP BRIDGE', err))
}

let i18nPromise = Promise.resolve()

// Set language cookie for mobile app webviews. For ?__rogueApp=… always mirror SSR l10n (including English);
// otherwise a stale `language=de` cookie is never cleared because we previously only set the cookie when lang !== 'en'.
if (rogue.isApp()) {
  setCookie('language', rogue.l10n.lang || 'en', 365)
} else if (rogue.l10n.lang && rogue.l10n.lang !== 'en') {
  setCookie('language', rogue.l10n.lang, 365)
}
// Legacy cookie handling for EUR/GBR sites without path prefix
if (['EUR', 'GBR'].includes(rogue.site.code) && !rogue.l10n.path) {
  setCookie('lang', rogue.l10n.lang, 365)
  setCookie('country', window.ROGUE.l10n.country, 365)
}

const router = createRouter(rogue, false)

rogue.log.info('Prepping app mount...')
Promise.allSettled([
  i18nPromise.then(() => rogue.log.debug('I18N finished')),
  initApp.then(() => rogue.log.debug('initApp finished')).catch(err => {
    rogue.log.warn('App bridge initialization failed (non-critical)', err)
    
  }),
]).then(async (results) => {
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      const names = ['i18n', 'app-bridge']
      rogue.log.warn(`${names[index] || 'Unknown'} promise rejected (non-critical)`, result.reason)
    }
  })
  rogue.log.info('App ready to mount')
  let root: Component | undefined = undefined
  const isRB = window.location.pathname?.toString().includes('/__rb__/rhpa')

  if (isRB) {
    root = RouterView
  }

  const { app } = createApp(rogue, router, root, window.__INITIAL_STATE__ as { pinia: string})

  const head = createHead()
  app.use(head)
  app.mixin(VueHeadMixin)

  app.provide('ROGUE', rogue)
  app.provide('rogue-icon-base-url', '/svc_spa/')
  // Content-hashed sprite URLs (see vite-plugin-icon-sprites.ts); Icon.vue falls back to the base URL for anything missing
  app.provide('rogue-icon-sprites', iconSprites)


  if (!app.config.globalProperties.$emitToApp) {
    app.config.globalProperties.$emitToApp = () => {
      // noop
    }
  }

  if (appContext) {
    app.config.globalProperties.$emitToApp = (message: string, data: any = {}) => {
      if (appBridge?.isReady()) {
        rogue.emitToApp(message, data)
      } else {
        appMessageQueue.push({ message, data })
      }
    }
  }

  // Handle all Vue errors
  configureErrorHandler(app, rogue)

  if (rogue.appContext && appBridge) {
    appBridge.init(app, router, rogue)
    appMessageQueue.forEach(({ message, data }) => rogue.emitToApp(message, data))
  }

  setBrowserVueApp(app)

  // Set the document language 
  document.documentElement.setAttribute('lang', window.ROGUE?.l10n?.lang || 'en')

  if (rogue.l10n.lang !== 'en') {
    i18nPromise = import(/* webpackChunkName: "i18n" */ '@/js/services/i18n/translate')
      .then(async (result) => {
        rogue.i18n = result.initRogueI18N(app, rogue, window.ROGUE.translations)

        const loadPromises = result.DEFAULT_NS.map((ns) => rogue.i18n.loadNamespace(ns))
        const shouldLoadEuFallback = result.NON_EU_SITE_CODES.includes(rogue.site?.code) && result.EU_FALLBACK_LANGUAGES.includes(rogue.l10n.lang)
        if (shouldLoadEuFallback) {
          result.DEFAULT_NS.forEach(ns => {
            const load = rogue.i18n.loadNamespace(`EU-${ns}`)
            if (load) loadPromises.push(load)
          })
        }
        await Promise.all(loadPromises)
      })
      .catch(err => {
        rogue.log.error('Failed to load i18n translations - app will continue without translations', err)
        app.directive('translate', {})
        
        return null
      })
  } else {
    app.directive('translate', {}) 
  }

  try {
    const [i18nResult, routerResult] = await Promise.allSettled([i18nPromise, router.isReady()])
    
    if (i18nResult.status === 'rejected') {
      rogue.log.warn('i18n promise rejected during mount preparation', i18nResult.reason)
    }
    if (routerResult.status === 'rejected') {
      rogue.log.error('Router ready promise rejected - this is critical', routerResult.reason)
    }

    app.mount('#app-wrapper')
    intercept(rogue)
  } catch (e) {
    const error = e as Error
    rogue.log.error(`Error mounting ${window.ROGUE.url}`, error, {
      url       : window.ROGUE.url,
      userAgent : navigator.userAgent,
      online    : navigator.onLine,
    })
    console.error(`Error mounting ${window.ROGUE.url}`, error)
  }


  if (isRB) {
    return
  }

  loadScript('recaptcha', rogue)


  if ('serviceWorker' in navigator) {
    import('./js/services/service-worker')
      .then(sw => sw.registerServiceWorker(rogue))
      .catch(err => {
        rogue.log.warn('Error loading service worker', err)
      })
  }
}).catch(err => {
  const error = err as Error
  rogue.log.error('Error with initial app mount (Promise.allSettled failed)', error, {
    url   : window.ROGUE.url,
    phase : 'pre-mount',
  })
  console.error('Error with app mount', error)
})
